import {
  $window, appBus, http, util, log
} from '@glu/core';
import routeManager from '@glu/core/src/routeManager';
import urls from 'system/urls';
import 'css/main.one-ptx.less';
import 'system/templateHelpers';
import gateway from 'system/gateway';

const updateHttpCodeHandlers = () => {
  http.statusCodeHandlers = {
    ...http.statusCodeHandlers,
    // TODO: move this to GLU, we should trigger http:404 with xhr for all errors.
    404: (xhr) => {
      // we should not show 'not found' page in some cases.
      if (xhr && xhr.skipDefaultBehavior) {
        return;
      }
      appBus.trigger('http:404', xhr);
    },
    403: (xhr) => {
      // we should not show 'forbidden' page in some cases.
      if (xhr && xhr.skipDefaultBehavior) {
        return;
      }

      appBus.trigger('http:403', xhr);
    },
    503: () => appBus.trigger('http:503')
  };
};

export default (Application, options = {}) => {
  let application;
  let isAuthenticated = false;
  const eventNames = options.globalEvents || [];

  const getApplicationInstance = config => (
    new Application({
      ...config,
      triggerGlobalEvent: (eventType) => {
        const eventName = `${application.getModuleName()}:${eventType}`;
        // eslint-disable-next-line no-undef
        const newArguments = [eventName].concat(util.rest(arguments, 1));
        config.interface.triggerGlobalEvent.apply(null, newArguments);
      }
    })
  );
  const initializeApplication = (config) => {
    const result = getApplicationInstance(config);
    if (config.domainUrl) {
      urls.setUrlPrefix(config.domainUrl);
    }
    config.interface.registerGlobalEvents(util.reduce(eventNames, (memo, eventName) => {
      const newMemo = memo;
      newMemo[`${result.getModuleName()}:${eventName}`] = () => (
        // eslint-disable-next-line no-undef
        appBus.trigger([eventName].concat(Array.prototype.slice.apply(arguments)))
      );
      return newMemo;
    }, {}));
    return result;
  };
  return {
    preload(config) {
      if (isAuthenticated) {
        return Promise.resolve();
      }
      application = initializeApplication(config);
      isAuthenticated = true;
      return application.authenticate(config).then(() => {
        if (!application.applicationPreLoaded) {
          return Promise.resolve();
        }
        return application.applicationPreLoaded(config);
      });
    },
    start(config = {}) {
      if (!isAuthenticated) {
        application = initializeApplication(config);
      }
      // establish connection between parent & child UIs
      appBus.listenTo(appBus, {
        'promise:error': config.interface.onError,
        unauthenticated: config.interface.unauthenticated,
        'http:401': config.interface.unauthenticated,
        'http:404': config.interface.notFound,
        'http:403': config.interface.forbidden,
        'http:500': config.interface.serverError,
        'http:503': config.interface.unavailable
      });
      $window.on('error.glu', this.showErrorDialog.bind(this));

      // initialize router
      application.getRouter(config);
      // will update http code handlers, this should be moved into GLU
      updateHttpCodeHandlers();
      const routes = application.getRoutes();
      routeManager.setRoot(config.root).setBaseRoute(config.baseRoute).registerRoutes(routes);
      const start = () => {
        const next = () => {
          const promise = new Promise((resolve) => {
            // temp method without router just to show something
            application.showLayout(config);
            resolve();
          });
          if (application.applicationHasStarted) {
            return promise.then(() => application.applicationHasStarted(config));
          }
          return promise;
        };
        if (application.applicationIsAboutToStart) {
          return application.applicationIsAboutToStart(config).then(next);
        }
        return next();
      };
      if (isAuthenticated) {
        return start();
      }
      isAuthenticated = true;
      return application.authenticate(config).then(start);
    },
    showErrorDialog() {
      log.debug('UnhandledJSException');
    },
    loadUrl() {
      application.loadUrl();
    },
    /**
     * This method is called every time when bearer token changes in keycloak.
     *
     * @param config {Object}
     */
    update(config) {
      return application.authenticate(config);
    },
    /**
     * Should be called whenever we stop entire sub application
     */
    stop(config) {
      gateway.stop();
      appBus.off();
      if (application.applicationIsAboutToStop) {
        application.applicationIsAboutToStop(config);
      }
      application.unmount();
      application.stop();
      delete window.Select2;
      delete window.$;
      config.interface.unregisterGlobalEvents(eventNames);
    }
  };
};
